// frontend/src/pages/FigmaComparison.jsx - Updated to match your project structure
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Upload, 
  Globe, 
  Play, 
  Square, 
  FileImage,
  Figma,
  Eye,
  Settings,
  Download,
  Share2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Zap,
  BarChart3,
  Palette,
  Type,
  Layout as LayoutIcon,
  Smartphone,
  Monitor,
  Tablet,
  RefreshCw,
  HelpCircle,
  ExternalLink,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import { useBrowserSession } from '../hooks/useBrowserSession'
import { useAuth } from '../hooks/useAuth'
import Modal from '../components/common/Modal'
import Button from '../components/common/Button'
import Input from '../components/common/Input'
import toast from 'react-hot-toast'

const FigmaComparison = () => {
  const { user } = useAuth()
  const { createSession, activeSession, updateSession } = useBrowserSession()
  
  // Core state
  const [comparisonMode, setComparisonMode] = useState('figma') // 'figma' | 'image'
  const [figmaUrl, setFigmaUrl] = useState('')
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [uploadedImage, setUploadedImage] = useState(null)
  const [isComparing, setIsComparing] = useState(false)
  const [comparisonResults, setComparisonResults] = useState(null)
  const [progress, setProgress] = useState({ message: '', percentage: 0 })
  
  // UI state
  const [showPermissionHelp, setShowPermissionHelp] = useState(false)
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false)
  const [selectedViewports, setSelectedViewports] = useState(['desktop', 'tablet', 'mobile'])
  const [comparisonOptions, setComparisonOptions] = useState({
    focus: [],
    include_responsive: true,
    similarity_threshold: 75
  })

  // File upload refs
  const fileInputRef = useRef(null)
  const dragDropRef = useRef(null)
  const [isDragging, setIsDragging] = useState(false)

  // WebSocket connection using your existing pattern
  const wsRef = useRef(null)
  const [connectionStatus, setConnectionStatus] = useState('disconnected')

  // WebSocket setup (matching your existing pattern)
  useEffect(() => {
    const connectWebSocket = () => {
      const sessionId = user?.id || 'figma-comparison'
      const wsUrl = `wss://api.testcrew.ai/ws/${sessionId}`
      
      try {
        wsRef.current = new WebSocket(wsUrl)
        
        wsRef.current.onopen = () => {
          setConnectionStatus('connected')
          console.log('✅ Figma WebSocket connected')
        }
        
        wsRef.current.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data)
            handleWebSocketMessage(message)
          } catch (error) {
            console.error('Error parsing WebSocket message:', error)
          }
        }
        
        wsRef.current.onclose = () => {
          setConnectionStatus('disconnected')
          console.log('❌ Figma WebSocket disconnected')
        }
        
        wsRef.current.onerror = (error) => {
          setConnectionStatus('error')
          console.error('WebSocket error:', error)
        }
        
      } catch (error) {
        console.error('Failed to create WebSocket:', error)
        setConnectionStatus('error')
      }
    }

    if (user?.id) {
      connectWebSocket()
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close()
      }
    }
  }, [user?.id])

  const handleWebSocketMessage = useCallback((message) => {
    console.log('📨 Figma comparison message:', message.type, message)

    switch (message.type) {
      case 'figma_permission_error':
        setIsComparing(false)
        setShowPermissionHelp(true)
        toast.error('Figma file access required. Please share your file.')
        break

      case 'vision_comparison_progress':
        setProgress({
          message: message.data.message,
          percentage: message.data.progress_percentage || 0
        })
        break

      case 'vision_comparison_completed':
        setIsComparing(false)
        setComparisonResults(message.data.results)
        setProgress({ message: 'Comparison completed!', percentage: 100 })
        toast.success(`Comparison completed! Similarity: ${message.data.results.overall_similarity || message.data.success_rate}%`)
        break

      case 'vision_comparison_error':
        setIsComparing(false)
        setProgress({ message: 'Comparison failed', percentage: 0 })
        toast.error(message.data.error)
        break

      case 'upload_completed':
        setUploadedImage({
          file_id: message.data.file_id,
          filename: message.data.filename || 'uploaded-design.png',
          url: message.data.url,
          thumbnail_url: message.data.thumbnail_url
        })
        toast.success('Image uploaded successfully!')
        break

      case 'figma_sharing_instructions':
        setShowPermissionHelp(true)
        break

      default:
        console.log('❓ Unknown Figma comparison message:', message.type)
    }
  }, [])

  const sendWebSocketMessage = (message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message))
      console.log('📤 Sent WebSocket message:', message.type)
    } else {
      console.error('WebSocket not connected')
      toast.error('Connection lost. Please refresh the page.')
    }
  }

  // File upload handlers
  const handleFileSelect = (file) => {
    if (!file) return
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB')
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      const imageData = e.target.result
      
      sendWebSocketMessage({
        type: 'upload_and_compare_image',
        data: {
          image_data: imageData,
          filename: file.name,
          website_url: websiteUrl
        }
      })
      
      toast.success('Uploading image...')
    }
    reader.readAsDataURL(file)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      handleFileSelect(files[0])
    }
  }

  // Validation functions
  const validateFigmaUrl = (url) => {
    return url.startsWith('https://www.figma.com/')
  }

  const validateWebsiteUrl = (url) => {
    try {
      new URL(url)
      return url.startsWith('http://') || url.startsWith('https://')
    } catch {
      return false
    }
  }

  // Comparison handlers
  const startComparison = async () => {
    if (!websiteUrl || !validateWebsiteUrl(websiteUrl)) {
      toast.error('Please enter a valid website URL')
      return
    }

    if (comparisonMode === 'figma') {
      if (!figmaUrl || !validateFigmaUrl(figmaUrl)) {
        toast.error('Please enter a valid Figma file URL')
        return
      }
    } else if (comparisonMode === 'image') {
      if (!uploadedImage) {
        toast.error('Please upload a design image')
        return
      }
    }

    setIsComparing(true)
    setProgress({ message: 'Starting comparison...', percentage: 0 })
    setComparisonResults(null)

    try {
      // Create session for tracking
      const session = await createSession('figma-comparison', 'Figma Design Comparison', {
        figma_url: figmaUrl,
        website_url: websiteUrl,
        comparison_mode: comparisonMode
      })

      // Send comparison request
      if (comparisonMode === 'figma') {
        sendWebSocketMessage({
          type: 'figma_website_comparison',
          data: {
            comparison_type: 'figma',
            figma_url: figmaUrl,
            website_url: websiteUrl,
            options: comparisonOptions,
            viewports: selectedViewports.map(name => ({ name, width: 1920, height: 1080 }))
          }
        })
      } else {
        sendWebSocketMessage({
          type: 'figma_website_comparison',
          data: {
            comparison_type: 'image',
            file_id: uploadedImage.file_id,
            website_url: websiteUrl,
            options: comparisonOptions
          }
        })
      }

    } catch (error) {
      setIsComparing(false)
      toast.error('Failed to start comparison')
      console.error('Comparison error:', error)
    }
  }

  const stopComparison = () => {
    sendWebSocketMessage({
      type: 'stop_comparison',
      data: {}
    })
    setIsComparing(false)
    setProgress({ message: 'Comparison stopped', percentage: 0 })
    toast.success('Comparison stopped')
  }

  const requestFigmaHelp = () => {
    sendWebSocketMessage({
      type: 'get_figma_help',
      data: {
        figma_url: figmaUrl,
        error_context: 'general'
      }
    })
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="flex items-center justify-center space-x-3 mb-4">
              <motion.div
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.5 }}
                className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg"
              >
                <Eye className="h-6 w-6 text-white" />
              </motion.div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Design Comparison
              </h1>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Compare your Figma designs with live websites using AI-powered visual analysis. 
              Get detailed insights, similarity scores, and actionable recommendations.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Input Section */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Connection Status */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${
                  connectionStatus === 'connected' ? 'bg-green-500' :
                  connectionStatus === 'connecting' ? 'bg-yellow-500' :
                  'bg-red-500'
                }`} />
                <span className="text-sm text-gray-600">
                  {connectionStatus === 'connected' ? 'Connected' :
                   connectionStatus === 'connecting' ? 'Connecting...' :
                   'Disconnected'}
                </span>
              </div>
            </div>
            
            {/* Comparison Mode Toggle */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Choose Comparison Method</h2>
              <div className="grid grid-cols-2 gap-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setComparisonMode('figma')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    comparisonMode === 'figma'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  <Figma className="h-8 w-8 mx-auto mb-2" />
                  <div className="font-medium">Figma File</div>
                  <div className="text-sm text-gray-500">Compare with Figma design</div>
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setComparisonMode('image')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    comparisonMode === 'image'
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  <FileImage className="h-8 w-8 mx-auto mb-2" />
                  <div className="font-medium">Upload Image</div>
                  <div className="text-sm text-gray-500">Compare with design image</div>
                </motion.button>
              </div>
            </motion.div>

            {/* Figma URL Input */}
            {comparisonMode === 'figma' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Figma Design File</h3>
                  <button
                    onClick={requestFigmaHelp}
                    className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    <HelpCircle className="h-4 w-4" />
                    <span>Need help?</span>
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div className="relative">
                    <Figma className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="url"
                      placeholder="https://www.figma.com/file/..."
                      value={figmaUrl}
                      onChange={(e) => setFigmaUrl(e.target.value)}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        figmaUrl && !validateFigmaUrl(figmaUrl)
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-300'
                      }`}
                    />
                  </div>
                  
                  {figmaUrl && !validateFigmaUrl(figmaUrl) && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="flex items-center space-x-2 text-red-600 text-sm"
                    >
                      <AlertCircle className="h-4 w-4" />
                      <span>Please enter a valid Figma file URL</span>
                    </motion.div>
                  )}
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <div className="font-medium mb-1">File Access Required</div>
                        <div>Make sure your Figma file is shared publicly or invite <strong>saquib@astronixtech.in</strong> as a viewer.</div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Image Upload */}
            {comparisonMode === 'image' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Design Image</h3>
                
                {!uploadedImage ? (
                  <motion.div
                    ref={dragDropRef}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    whileHover={{ scale: 1.01 }}
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer ${
                      isDragging
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50'
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <div className="text-lg font-medium text-gray-900 mb-2">
                      Drop your design image here
                    </div>
                    <div className="text-gray-500 mb-4">
                      or click to browse files
                    </div>
                    <div className="text-sm text-gray-400">
                      Supports PNG, JPG, GIF, WebP (max 10MB)
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-green-50 border border-green-200 rounded-lg p-4"
                  >
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="h-6 w-6 text-green-600" />
                      <div className="flex-1">
                        <div className="font-medium text-green-900">{uploadedImage.filename}</div>
                        <div className="text-sm text-green-700">Image uploaded successfully</div>
                      </div>
                      <button
                        onClick={() => setUploadedImage(null)}
                        className="text-green-600 hover:text-green-700 p-1"
                      >
                        <XCircle className="h-5 w-5" />
                      </button>
                    </div>
                  </motion.div>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileSelect(e.target.files[0])}
                  className="hidden"
                />
              </motion.div>
            )}

            {/* Website URL Input */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Website to Compare</h3>
              
              <div className="space-y-4">
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="url"
                    placeholder="https://example.com"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      websiteUrl && !validateWebsiteUrl(websiteUrl)
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                    }`}
                  />
                </div>
                
                {websiteUrl && !validateWebsiteUrl(websiteUrl) && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="flex items-center space-x-2 text-red-600 text-sm"
                  >
                    <AlertCircle className="h-4 w-4" />
                    <span>Please enter a valid website URL</span>
                  </motion.div>
                )}
              </div>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex items-center space-x-4"
            >
              {!isComparing ? (
                <Button
                  onClick={startComparison}
                  disabled={
                    connectionStatus !== 'connected' ||
                    !websiteUrl ||
                    !validateWebsiteUrl(websiteUrl) ||
                    (comparisonMode === 'figma' && (!figmaUrl || !validateFigmaUrl(figmaUrl))) ||
                    (comparisonMode === 'image' && !uploadedImage)
                  }
                  className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-medium"
                >
                  <Zap className="h-5 w-5" />
                  <span>Start Comparison</span>
                </Button>
              ) : (
                <Button
                  onClick={stopComparison}
                  variant="danger"
                  className="flex items-center space-x-2 px-6 py-3"
                >
                  <Square className="h-5 w-5" />
                  <span>Stop Comparison</span>
                </Button>
              )}
              
              {comparisonResults && (
                <Button
                  onClick={() => {
                    setComparisonResults(null)
                    setProgress({ message: '', percentage: 0 })
                  }}
                  variant="outline"
                  className="flex items-center space-x-2 px-6 py-3"
                >
                  <RefreshCw className="h-5 w-5" />
                  <span>New Comparison</span>
                </Button>
              )}
            </motion.div>
          </div>

          {/* Progress & Results Section */}
          <div className="space-y-6">
            
            {/* Progress Indicator */}
            {(isComparing || progress.message) && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Progress</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    {isComparing ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                        className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full"
                      />
                    ) : (
                      <CheckCircle className="h-6 w-6 text-green-500" />
                    )}
                    <span className="text-sm font-medium text-gray-700">
                      {progress.message || 'Ready to compare'}
                    </span>
                  </div>
                  
                  {progress.percentage > 0 && (
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${progress.percentage}%` }}
                        transition={{ duration: 0.5 }}
                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full"
                      />
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Quick Results Summary */}
            {comparisonResults && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Results Summary</h3>
                
                <div className="space-y-4">
                  {/* Overall Similarity */}
                  <div className="text-center p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
                    <div className="text-3xl font-bold text-gray-900 mb-1">
                      {comparisonResults.overall_similarity || comparisonResults.success_rate}%
                    </div>
                    <div className="text-sm text-gray-600">Overall Similarity</div>
                  </div>

                  {/* Quality Rating */}
                  {comparisonResults.quality_rating && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-700">Quality Rating</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        comparisonResults.quality_rating === 'Excellent' ? 'bg-green-100 text-green-800' :
                        comparisonResults.quality_rating === 'Good' ? 'bg-blue-100 text-blue-800' :
                        comparisonResults.quality_rating === 'Fair' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {comparisonResults.quality_rating}
                      </span>
                    </div>
                  )}

                  {/* Quick Actions */}
                  <div className="flex space-x-2 pt-4 border-t border-gray-200">
                    <Button
                      onClick={() => {
                        // Scroll to detailed results
                        document.getElementById('detailed-results')?.scrollIntoView({ behavior: 'smooth' })
                      }}
                      variant="outline"
                      size="sm"
                      className="flex-1"
                    >
                      <BarChart3 className="h-4 w-4 mr-2" />
                      View Details
                    </Button>
                    <Button
                      onClick={() => {
                        // Download results as JSON
                        const blob = new Blob([JSON.stringify(comparisonResults, null, 2)], {
                          type: 'application/json'
                        })
                        const url = URL.createObjectURL(blob)
                        const a = document.createElement('a')
                        a.href = url
                        a.download = 'comparison-results.json'
                        a.click()
                        URL.revokeObjectURL(url)
                      }}
                      variant="outline"
                      size="sm"
                      className="flex-1"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Tips & Information */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl border border-blue-200 p-6"
            >
              <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
                <Zap className="h-5 w-5 mr-2" />
                Pro Tips
              </h3>
              <div className="space-y-3 text-sm text-blue-800">
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                  <div>Use high-resolution designs for better accuracy</div>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                  <div>Test multiple viewports to catch responsive issues</div>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                  <div>Share Figma files publicly for instant access</div>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                  <div>Check CSS recommendations for quick fixes</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Detailed Results */}
        {comparisonResults && (
          <motion.div
            id="detailed-results"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-12"
          >
            <DetailedResults results={comparisonResults} />
          </motion.div>
        )}
      </div>

      {/* Permission Help Modal */}
      <Modal
        isOpen={showPermissionHelp}
        onClose={() => setShowPermissionHelp(false)}
        title="Figma File Access Required"
        size="lg"
      >
        <FigmaPermissionHelp 
          onClose={() => setShowPermissionHelp(false)}
          figmaUrl={figmaUrl}
        />
      </Modal>
    </div>
  )
}

// Detailed Results Component
const DetailedResults = ({ results }) => {
  const [activeTab, setActiveTab] = useState('overview')

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'issues', label: 'Issues', icon: AlertCircle },
    { id: 'recommendations', label: 'Recommendations', icon: Zap },
    { id: 'css', label: 'CSS Fixes', icon: LayoutIcon }
  ]

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <div className="flex space-x-8 px-6">
          {tabs.map(({ id, label, icon: Icon }) => (
            <motion.button
              key={id}
              whileHover={{ y: -2 }}
              onClick={() => setActiveTab(id)}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-all ${
                activeTab === id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Icon className="h-4 w-4" />
                <span>{label}</span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'overview' && <OverviewTab results={results} />}
            {activeTab === 'issues' && <IssuesTab results={results} />}
            {activeTab === 'recommendations' && <RecommendationsTab results={results} />}
            {activeTab === 'css' && <CSSFixesTab results={results} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}

// Overview Tab
const OverviewTab = ({ results }) => (
  <div className="space-y-6">
    {/* Main Metrics */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="text-center p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg">
        <div className="text-3xl font-bold text-gray-900 mb-2">
          {results.overall_similarity || results.success_rate}%
        </div>
        <div className="text-sm text-gray-600">Overall Similarity</div>
      </div>
      
      <div className="text-center p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
        <div className="text-3xl font-bold text-gray-900 mb-2">
          {results.specific_issues?.length || results.major_differences?.length || 0}
        </div>
        <div className="text-sm text-gray-600">Issues Found</div>
      </div>
      
      <div className="text-center p-6 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg">
        <div className="text-3xl font-bold text-gray-900 mb-2">
          {results.recommendations?.length || results.css_fixes?.length || 0}
        </div>
        <div className="text-sm text-gray-600">Recommendations</div>
      </div>
    </div>

    {/* Category Breakdown */}
    {results.category_scores && (
      <div>
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Category Breakdown</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(results.category_scores).map(([key, value]) => (
            <div key={key} className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 capitalize">
                  {key.replace(/_/g, ' ')}
                </span>
                <span className="text-sm font-bold text-gray-900">
                  {Math.round(value)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${value}%` }}
                  transition={{ duration: 1, delay: 0.1 }}
                  className={`h-2 rounded-full ${
                    value >= 80 ? 'bg-green-500' :
                    value >= 60 ? 'bg-yellow-500' :
                    'bg-red-500'
                  }`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Viewport Analysis */}
    {results.viewport_analysis && (
      <div>
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Viewport Analysis</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(results.viewport_analysis).map(([viewport, data]) => (
            <div key={viewport} className="p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                {viewport === 'desktop' && <Monitor className="h-5 w-5 text-gray-600" />}
                {viewport === 'tablet' && <Tablet className="h-5 w-5 text-gray-600" />}
                {viewport === 'mobile' && <Smartphone className="h-5 w-5 text-gray-600" />}
                <span className="font-medium text-gray-900 capitalize">{viewport}</span>
              </div>
              {typeof data === 'object' ? (
                <>
                  <div className="text-2xl font-bold text-gray-900 mb-1">
                    {data.similarity}%
                  </div>
                  <div className="text-sm text-gray-600">{data.notes}</div>
                </>
              ) : (
                <div className="text-sm text-gray-600">{data}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
)

// Issues Tab
const IssuesTab = ({ results }) => {
  const issues = results.specific_issues || results.major_differences || []
  const minorIssues = results.minor_differences || []

  return (
    <div className="space-y-6">
      {/* Major Issues */}
      {issues.length > 0 && (
        <div>
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            Major Issues ({issues.length})
          </h4>
          <div className="space-y-3">
            {issues.map((issue, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="p-4 bg-red-50 border border-red-200 rounded-lg"
              >
                {typeof issue === 'object' ? (
                  <>
                    <div className="flex items-start justify-between mb-2">
                      <div className="font-medium text-red-900">{issue.issue || issue.element}</div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        issue.priority === 'High' ? 'bg-red-100 text-red-800' :
                        issue.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {issue.priority || 'Medium'}
                      </span>
                    </div>
                    {issue.location && (
                      <div className="text-sm text-red-700 mb-2">
                        <strong>Location:</strong> {issue.location}
                      </div>
                    )}
                    {issue.description && (
                      <div className="text-sm text-red-700">{issue.description}</div>
                    )}
                  </>
                ) : (
                  <div className="text-red-900">{issue}</div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Minor Issues */}
      {minorIssues.length > 0 && (
        <div>
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <AlertCircle className="h-5 w-5 text-yellow-500 mr-2" />
            Minor Issues ({minorIssues.length})
          </h4>
          <div className="space-y-3">
            {minorIssues.map((issue, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg"
              >
                <div className="text-yellow-900">{issue}</div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {issues.length === 0 && minorIssues.length === 0 && (
        <div className="text-center py-12">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Issues Found!</h3>
          <p className="text-gray-600">Your implementation matches the design very well.</p>
        </div>
      )}
    </div>
  )
}

// Recommendations Tab
const RecommendationsTab = ({ results }) => {
  const recommendations = results.recommendations || []

  return (
    <div className="space-y-4">
      {recommendations.length > 0 ? (
        recommendations.map((rec, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="p-4 bg-blue-50 border border-blue-200 rounded-lg"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="font-medium text-blue-900">
                {rec.suggestion || rec.issue}
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                rec.priority === 'High' ? 'bg-red-100 text-red-800' :
                rec.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                'bg-green-100 text-green-800'
              }`}>
                {rec.priority}
              </span>
            </div>
            
            {rec.category && (
              <div className="text-sm text-blue-700 mb-2">
                <strong>Category:</strong> {rec.category}
              </div>
            )}
            
            {rec.impact && (
              <div className="text-sm text-blue-700 mb-2">
                <strong>Impact:</strong> {rec.impact}
              </div>
            )}
            
            {rec.solution && (
              <div className="text-sm text-blue-700 mb-2">
                <strong>Solution:</strong> {rec.solution}
              </div>
            )}
            
            {rec.expected_improvement && (
              <div className="text-sm text-blue-700">
                <strong>Expected Improvement:</strong> {rec.expected_improvement}
              </div>
            )}
          </motion.div>
        ))
      ) : (
        <div className="text-center py-12">
          <Zap className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Recommendations</h3>
          <p className="text-gray-600">Your implementation is already well-optimized!</p>
        </div>
      )}
    </div>
  )
}

// CSS Fixes Tab
const CSSFixesTab = ({ results }) => {
  const cssFixes = results.css_fixes || []

  return (
    <div className="space-y-4">
      {cssFixes.length > 0 ? (
        cssFixes.map((fix, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="p-4 bg-gray-50 border border-gray-200 rounded-lg"
          >
            <div className="font-medium text-gray-900 mb-2">{fix.selector}</div>
            
            {fix.property && (
              <div className="text-sm text-gray-600 mb-2">
                <strong>Property:</strong> {fix.property}
              </div>
            )}
            
            {fix.current_value && (
              <div className="text-sm text-gray-600 mb-2">
                <strong>Current:</strong> {fix.current_value}
              </div>
            )}
            
            {fix.recommended_value && (
              <div className="text-sm text-gray-600 mb-3">
                <strong>Recommended:</strong> {fix.recommended_value}
              </div>
            )}
            
            {fix.css_code && (
              <div className="bg-gray-900 text-gray-100 p-3 rounded-md font-mono text-sm overflow-x-auto">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-green-400">/* CSS Fix */</span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(fix.css_code)
                      toast.success('CSS copied to clipboard!')
                    }}
                    className="text-blue-400 hover:text-blue-300 text-xs"
                  >
                    Copy
                  </button>
                </div>
                <div>{fix.css_code}</div>
              </div>
            )}
          </motion.div>
        ))
      ) : (
        <div className="text-center py-12">
          <LayoutIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No CSS Fixes Needed</h3>
          <p className="text-gray-600">Your styling is spot on!</p>
        </div>
      )}
    </div>
  )
}

// Figma Permission Help Component
const FigmaPermissionHelp = ({ onClose, figmaUrl }) => {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <Figma className="h-16 w-16 text-blue-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Share Your Figma File
        </h3>
        <p className="text-gray-600">
          To analyze your design, we need access to your Figma file. Choose one of the options below:
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Option 1: Public Link */}
        <div className="p-6 border-2 border-blue-200 rounded-lg bg-blue-50">
          <div className="text-center mb-4">
            <Share2 className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <h4 className="font-semibold text-blue-900">Make Public (Recommended)</h4>
          </div>
          <div className="space-y-3 text-sm text-blue-800">
            <div className="flex items-start space-x-2">
              <div className="w-5 h-5 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">1</div>
              <div>Click the <strong>Share</strong> button in your Figma file</div>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-5 h-5 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">2</div>
              <div>Change to <strong>"Anyone with the link can view"</strong></div>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-5 h-5 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">3</div>
              <div>Copy the URL and paste it above</div>
            </div>
          </div>
          <div className="mt-4 p-3 bg-blue-100 rounded-lg">
            <div className="text-xs text-blue-700">
              ⚡ <strong>Fastest option</strong> - Works instantly!
            </div>
          </div>
        </div>

        {/* Option 2: Invite */}
        <div className="p-6 border-2 border-purple-200 rounded-lg bg-purple-50">
          <div className="text-center mb-4">
            <Eye className="h-8 w-8 text-purple-600 mx-auto mb-2" />
            <h4 className="font-semibold text-purple-900">Invite Our System</h4>
          </div>
          <div className="space-y-3 text-sm text-purple-800">
            <div className="flex items-start space-x-2">
              <div className="w-5 h-5 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">1</div>
              <div>Click <strong>Share</strong> → <strong>Invite people</strong></div>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-5 h-5 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">2</div>
              <div>Enter: <strong>saquib@astronixtech.in</strong></div>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-5 h-5 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">3</div>
              <div>Set permission to <strong>"Can view"</strong></div>
            </div>
          </div>
          <div className="mt-4 p-3 bg-purple-100 rounded-lg">
            <div className="text-xs text-purple-700">
              🔒 <strong>Keeps file private</strong> - You control access
            </div>
          </div>
        </div>
      </div>

      {/* Current File Info */}
      {figmaUrl && (
        <div className="p-4 bg-gray-50 rounded-lg">
          <h5 className="font-medium text-gray-900 mb-2">Current Figma File:</h5>
          <div className="flex items-center space-x-2">
            <code className="flex-1 text-sm bg-white p-2 rounded border text-gray-700">
              {figmaUrl}
            </code>
            <button
              onClick={() => window.open(figmaUrl, '_blank')}
              className="text-blue-600 hover:text-blue-700 p-1"
              title="Open in Figma"
            >
              <ExternalLink className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <div className="flex space-x-3">
        <Button onClick={onClose} variant="outline" className="flex-1">
          Cancel
        </Button>
        <Button 
          onClick={() => {
            toast.success('Instructions sent! Please check your Figma file sharing settings.')
            onClose()
          }}
          className="flex-1"
        >
          Got it!
        </Button>
      </div>
    </div>
  )
}

export default FigmaComparison